// frontend/src/components/ROTeam/TeamRestDayPanel.js
import React, { useState } from 'react';
import { CalendarDays, ShieldAlert } from 'lucide-react';
import useMyTeamRestDayData from '../../hooks/useMyTeamRestDayData';
import RestDayStatsCards from '../../pages/RestDayMonitoring/components/RestDayStatsCards';
import EmployeeRestDayTable from '../../pages/RestDayMonitoring/components/EmployeeRestDayTable';
import EmployeeRestDayDetailModal from '../../pages/RestDayMonitoring/components/EmployeeRestDayDetailModal';

/**
 * TeamRestDayPanel — manager-scoped 12-day rest-day interval monitoring
 * for the "My Team" dashboard. Reuses the admin RestDayMonitoring stat
 * cards, employee table, and detail modal verbatim, fed by the
 * team-scoped `useMyTeamRestDayData` hook so a Reporting Officer / HOD
 * sees only the workmen under their care. Read-only: no alerts / coverage.
 */
const TeamRestDayPanel = () => {
  const {
    stats,
    employees,
    loading,
    forbidden,
    filters,
    updateFilters,
    getEmployeeDetail,
  } = useMyTeamRestDayData();

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  if (forbidden) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-700 mb-2">No Team to Monitor</h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Rest-day monitoring is available to Reporting Officers, Trade Leaders,
          Managers, and Heads of Department. You don't currently have any team
          members under your care.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-header */}
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <CalendarDays className="w-4 h-4 text-red-600" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            Rest Day Interval
          </h2>
          <p className="text-xs sm:text-sm text-gray-500">
            MOM Employment Act s36 — no more than 12 consecutive working days for your team.
          </p>
        </div>
      </div>

      <RestDayStatsCards
        stats={stats}
        loading={loading}
        onCardClick={(value) => updateFilters({ status: value })}
        activeFilter={filters.status}
      />

      <EmployeeRestDayTable
        employees={employees}
        loading={loading}
        filters={filters}
        onFilterChange={updateFilters}
        onViewEmployee={(emp) => setSelectedEmployee(emp)}
      />

      <EmployeeRestDayDetailModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
        getEmployeeDetail={getEmployeeDetail}
      />
    </div>
  );
};

export default TeamRestDayPanel;
